import React from 'react';
import { Table, Button } from 'react-bootstrap';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { BreakfastBookingResponse, BreakfastBookingResponseData } from '../../../../services/breakfast-report/response/BookingResponse';
import { format } from 'date-fns';

interface BreakfastTableReportProps {
    data: BreakfastBookingResponse;
}

const BreakfastTableReport: React.FC<BreakfastTableReportProps> = ({ data }) => {
    console.log("BreakfastTableReport data:", data);

    const columnsReservations = [
        { label: "Guest Name", key: "guest_name" },
        { label: "Booking Number", key: "booking_number" },
        { label: "Arrival", key: "arrival_date" },
        { label: "Departure", key: "departure_date" },
        { label: "Room Number", key: "room_number" },
        { label: "Room Type", key: "room_type_name" },
        { label: "Incl. Days", key: "incl_days" },
        { label: "Total Adults", key: "total_adults" },
        { label: "Total Children", key: "total_children" },
        { label: "Revenue Adjustment (IDR)", key: "revenue_adjustment" },
    ];

    // Export to PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Breakfast Report", 14, 10);

        const tableData = data.data.map(row => columnsReservations.map(col => {
            if (col.key === 'revenue_adjustment') {
                return formatPrice(Number(row[col.key as keyof BreakfastBookingResponseData]));
            }
            if (col.key === 'arrival_date' || col.key === 'departure_date') {
                return format(new Date(row[col.key as keyof BreakfastBookingResponseData]), 'dd MMM yyyy');
            }
            return row[col.key as keyof BreakfastBookingResponseData] || '-';
        }));

        autoTable(doc, {
            head: [columnsReservations.map(col => col.label)],
            body: tableData,
            foot: [
                [
                    "TOTAL",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    data.total_adults,
                    data.total_children,
                    formatPrice(data.total_revenue_adjustment),
                ]
            ]
        });

        doc.save("BreakfastReport.pdf");
    };

    // Export to Excel
    const exportToExcel = () => {
        const wsData = [
            columnsReservations.map(col => col.label),
            ...data.data.map(row => columnsReservations.map(col => {
                if (col.key === 'revenue_adjustment') {
                    return formatPrice(Number(row[col.key as keyof BreakfastBookingResponseData]));
                }
                if (col.key === 'arrival_date' || col.key === 'departure_date') {
                    return format(new Date(row[col.key as keyof BreakfastBookingResponseData]), 'dd MMM yyyy');
                }
                return row[col.key as keyof BreakfastBookingResponseData] || '-';
            })),
            [
                "TOTAL",
                "",
                "",
                "",
                "",
                "",
                "",
                data.total_adults,
                data.total_children,
                formatPrice(data.total_revenue_adjustment),
            ]
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, "BreakfastReport.xlsx");
    };

    const formatCellValue = (row: BreakfastBookingResponseData, key: string) => {
        const value = row[key as keyof BreakfastBookingResponseData];

        if (key === 'revenue_adjustment') {
            return formatPrice(parseFloat(String(value)) || 0);
        }

        if (key === 'arrival_date' || key === 'departure_date') {
            return format(new Date(String(value)), 'dd MMM yyyy');
        }

        return value || '-';
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columnsReservations.map((col, index) => (
                                <th key={index}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.data.length > 0 ? (
                            [...data.data]
                                .sort((a, b) => new Date(a.arrival_date).getTime() - new Date(b.arrival_date).getTime())
                                .map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {columnsReservations.map((col, colIndex) => (
                                            <td key={colIndex}>
                                                {col.key === 'revenue_adjustment'
                                                    ? formatCellValue(row, col.key)
                                                    : col.key === 'arrival_date' || col.key === 'departure_date'
                                                        ? formatCellValue(row, col.key)
                                                        : row[col.key as keyof BreakfastBookingResponseData] || '-'}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                        ) : (
                            <tr>
                                <td colSpan={columnsReservations.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={7}><strong>TOTAL</strong></td>
                            <td><strong>{data.total_adults}</strong></td>
                            <td><strong>{data.total_children}</strong></td>
                            <td><strong>{formatPrice(data.total_revenue_adjustment || 0)}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default BreakfastTableReport;
